import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CalendarClock, Clock, Globe, Check, ArrowRight, Loader2,
  ShieldCheck, UserCheck, MessageCircle, CheckCircle2,
} from "lucide-react";
import Nav from "@/components/marketing/Nav";
import Footer from "@/components/marketing/Footer";
import { Reveal } from "@/components/Reveal";
import api, { apiErrorMessage } from "@/lib/api";

const HERO_IMG =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_visa-workflow-6/artifacts/mawzi01o_image.png";

const TOPICS = [
  "General visa consultation",
  "United Kingdom visa",
  "Germany visa",
  "Schengen visa",
  "United States visa",
  "Agency partnership",
  "Other",
];

function getTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

const EMPTY_FORM = { name: "", email: "", phone: "", topic: TOPICS[0], message: "" };

export default function Consultation() {
  const [tz] = useState(getTimezone());
  const [days, setDays] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/consultations/days", { params: { tz } })
      .then((r) => {
        setDays(r.data.days || []);
        if (r.data.days && r.data.days.length) setSelectedDate(r.data.days[0].date);
      })
      .catch(() => setError("Could not load available dates. Please try again."));
  }, [tz]);

  const loadSlots = useCallback((date) => {
    if (!date) return;
    setSlotsLoading(true);
    setSelectedSlot(null);
    api.get("/consultations/slots", { params: { date, tz } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [tz]);

  useEffect(() => { loadSlots(selectedDate); }, [selectedDate, loadSlots]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const canSubmit = form.name.trim() && form.email.trim() && selectedSlot && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!selectedSlot) { setError("Please choose a time."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/consultations", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        topic: form.topic,
        message: form.message.trim() || null,
        start_utc: selectedSlot.start_utc,
        tz,
      });
      setResult(data);
      window.scrollTo(0, 0);
    } catch (err) {
      if (err?.response?.status === 409) {
        setError("That time was just taken. Please pick another slot.");
        loadSlots(selectedDate);
      } else {
        setError(apiErrorMessage(err?.response?.data?.detail));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-softcream">
      <Nav />

      {/* HERO */}
      <section className="border-b border-hborder bg-white">
        <div className="mx-auto grid max-w-page items-center gap-8 px-5 py-12 sm:px-8 md:py-16 lg:grid-cols-2 lg:px-12">
          <Reveal>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-coral sm:text-sm">
              Free 30-minute consultation
            </p>
            <h1 className="font-serif text-4xl leading-[1.1] tracking-tight text-heading sm:text-5xl">
              Talk to a visa specialist.
            </h1>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-muted sm:text-lg">
              Pick a time that works for you. A real member of the Holavisto team will walk you
              through your destination, the documents you'll need and how we can prepare your
              application. No obligation.
            </p>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-navy">
              {["Human support", "No sales pressure", "Clear next steps"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <CheckCircle2 size={15} className="text-tblue" /> {t}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1} className="hidden lg:block">
            <div className="relative">
              <div className="absolute right-4 top-2 h-[300px] w-[88%] rounded-md bg-cream" aria-hidden />
              <div className="relative flex h-[300px] items-end justify-center">
                <img src={HERO_IMG} alt="Holavisto travel specialist" className="h-full w-auto object-contain drop-" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BOOKING */}
      <section className="mx-auto max-w-page px-5 py-12 sm:px-8 md:py-16 lg:px-12">
        {result ? (
          <SuccessCard result={result} form={form} onReset={() => {
            setResult(null); setForm(EMPTY_FORM); setSelectedSlot(null); loadSlots(selectedDate);
          }} />
        ) : (
          <div className="grid gap-8 lg:grid-cols-5">
            {/* Left info */}
            <div className="min-w-0 lg:col-span-2">
              <div className="rounded-md border border-hborder bg-white p-6 sm:p-8">
                <h2 className="font-serif text-2xl tracking-tight text-heading">What to expect</h2>
                <ul className="mt-5 space-y-4">
                  {[
                    { Icon: UserCheck, t: "A real specialist", b: "Not a bot. A Holavisto team member who knows your corridor." },
                    { Icon: Clock, t: "30 focused minutes", b: "We review your trip, requirements and the best way to prepare." },
                    { Icon: ShieldCheck, t: "Honest guidance", b: "We tell you what's needed and what to do next. Government decides the outcome." },
                  ].map(({ Icon, t, b }) => (
                    <li key={t} className="flex gap-3">
                      <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded bg-cream text-navy">
                        <Icon size={18} />
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-heading">{t}</p>
                        <p className="text-sm leading-relaxed text-muted">{b}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 flex items-center gap-2 rounded-md bg-softcream px-4 py-3 text-sm text-navy">
                  <Globe size={16} className="text-tblue" />
                  Times shown in your timezone: <span className="font-semibold">{tz}</span>
                </div>
              </div>
            </div>

            {/* Right booking */}
            <div className="min-w-0 lg:col-span-3">
              <form onSubmit={submit} className="rounded-md border border-hborder bg-white p-6 sm:p-8">
                {/* Step 1 date */}
                <div className="flex items-center gap-2">
                  <CalendarClock size={18} className="text-coral" />
                  <h3 className="text-sm font-bold uppercase tracking-[0.12em] text-navy">1 · Pick a date</h3>
                </div>
                <div className="mt-4 flex gap-2.5 overflow-x-auto pb-2" data-testid="day-strip">
                  {days.map((d) => {
                    const active = d.date === selectedDate;
                    return (
                      <button
                        key={d.date}
                        type="button"
                        data-testid={`day-${d.date}`}
                        onClick={() => setSelectedDate(d.date)}
                        className={`flex min-w-[64px] flex-col items-center rounded-md border px-3 py-2.5 transition-colors ${
                          active ? "border-navy bg-navy text-white" : "border-hborder bg-white text-navy hover:border-navy"
                        }`}>
                        <span className="text-[11px] font-medium uppercase opacity-80">{d.weekday}</span>
                        <span className="text-lg font-bold leading-tight">{d.day}</span>
                        <span className="text-[11px] font-medium opacity-80">{d.month}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Step 2 time */}
                <div className="mt-7 flex items-center gap-2">
                  <Clock size={18} className="text-coral" />
                  <h3 className="text-sm font-bold uppercase tracking-[0.12em] text-navy">2 · Pick a time</h3>
                </div>
                <div className="mt-4 min-h-[64px]">
                  {slotsLoading ? (
                    <div className="flex items-center gap-2 py-4 text-sm text-muted">
                      <Loader2 size={16} className="animate-spin" /> Loading available times…
                    </div>
                  ) : slots.length === 0 ? (
                    <p className="py-4 text-sm text-muted" data-testid="no-slots">
                      No times available for this day. Please choose another date.
                    </p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-4" data-testid="slot-grid">
                      {slots.map((s) => {
                        const active = selectedSlot?.start_utc === s.start_utc;
                        return (
                          <button
                            key={s.start_utc}
                            type="button"
                            data-testid={`slot-${s.label}`}
                            onClick={() => setSelectedSlot(s)}
                            className={`rounded-lg border px-2 py-2.5 text-sm font-semibold transition-colors ${
                              active ? "border-tblue bg-tblue text-white" : "border-hborder bg-white text-navy hover:border-tblue"
                            }`}>
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Step 3 details */}
                <div className="mt-7 flex items-center gap-2">
                  <MessageCircle size={18} className="text-coral" />
                  <h3 className="text-sm font-bold uppercase tracking-[0.12em] text-navy">3 · Your details</h3>
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-1">
                    <label className="mb-1 block text-sm font-medium text-navy">Full name *</label>
                    <input data-testid="field-name" value={form.name} onChange={setField("name")} required maxLength={120}
                      className="w-full rounded-lg border border-hborder bg-white px-3.5 py-2.5 text-sm text-navy outline-none focus:border-tblue" />
                  </div>
                  <div className="sm:col-span-1">
                    <label className="mb-1 block text-sm font-medium text-navy">Email *</label>
                    <input data-testid="field-email" type="email" value={form.email} onChange={setField("email")} required
                      className="w-full rounded-lg border border-hborder bg-white px-3.5 py-2.5 text-sm text-navy outline-none focus:border-tblue" />
                  </div>
                  <div className="sm:col-span-1">
                    <label className="mb-1 block text-sm font-medium text-navy">Phone (optional)</label>
                    <input data-testid="field-phone" value={form.phone} onChange={setField("phone")} maxLength={40}
                      className="w-full rounded-lg border border-hborder bg-white px-3.5 py-2.5 text-sm text-navy outline-none focus:border-tblue" />
                  </div>
                  <div className="sm:col-span-1">
                    <label className="mb-1 block text-sm font-medium text-navy">Topic</label>
                    <select data-testid="field-topic" value={form.topic} onChange={setField("topic")}
                      className="w-full rounded-lg border border-hborder bg-white px-3.5 py-2.5 text-sm text-navy outline-none focus:border-tblue">
                      {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-navy">Anything we should know? (optional)</label>
                    <textarea data-testid="field-message" value={form.message} onChange={setField("message")} rows={3} maxLength={2000}
                      className="w-full rounded-lg border border-hborder bg-white px-3.5 py-2.5 text-sm text-navy outline-none focus:border-tblue" />
                  </div>
                </div>

                {selectedSlot && (
                  <div className="mt-5 flex items-center gap-2 rounded-md bg-cream px-4 py-3 text-sm font-medium text-navy" data-testid="selected-summary">
                    <Check size={16} className="text-tblue" />
                    Selected: {days.find((d) => d.date === selectedDate)?.weekday}, {selectedDate} at {selectedSlot.label} to {selectedSlot.end_label} ({tz})
                  </div>
                )}

                {error && <p className="mt-4 text-sm font-medium text-coral" data-testid="booking-error">{error}</p>}

                <button
                  type="submit"
                  data-testid="confirm-booking-btn"
                  disabled={!canSubmit}
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded bg-brandyellow px-7 py-3.5 text-sm font-semibold text-heading transition-all duration-300   disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto">
                  {submitting ? <><Loader2 size={17} className="animate-spin" /> Booking…</> : <>Confirm booking <ArrowRight size={17} /></>}
                </button>
                <p className="mt-4 text-xs leading-relaxed text-muted">
                  Visa decisions are made by the relevant government authority. Holavisto provides
                  visa preparation and support and does not guarantee an outcome.
                </p>
              </form>
            </div>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}

function SuccessCard({ result, form, onReset }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="mx-auto max-w-xl rounded-md border border-hborder bg-white p-8 text-center sm:p-10"
      data-testid="booking-success">
      <div className="mx-auto grid h-16 w-16 place-items-center rounded bg-cream text-navy">
        <Check size={30} className="text-tblue" />
      </div>
      <h2 className="mt-5 font-serif text-3xl tracking-tight text-heading">You're booked</h2>
      <p className="mt-3 text-base leading-relaxed text-muted">
        Thanks, {form.name.split(" ")[0] || form.name}. Your consultation is confirmed for:
      </p>
      <div className="mx-auto mt-5 max-w-sm rounded-md bg-softcream px-5 py-4 text-left">
        <p className="text-sm text-muted">When</p>
        <p className="text-lg font-semibold text-heading">{result.when_label}</p>
        <p className="mt-1 text-xs text-muted">Timezone: {result.tz}</p>
      </div>
      <p className="mt-5 text-sm leading-relaxed text-navy">
        We've emailed a confirmation to <span className="font-semibold">{form.email}</span> and our
        team will send your meeting link before the call.
      </p>
      {result.email_status === "failed" && (
        <p className="mt-3 text-xs text-coral">
          (We saved your booking but the confirmation email is delayed. Our team has your details and will reach out.)
        </p>
      )}
      <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Link to="/" className="inline-flex items-center gap-2 rounded border border-hborder bg-white px-6 py-3 text-sm font-semibold text-navy transition-colors hover:border-navy">
          Back to home
        </Link>
        <button onClick={onReset} className="inline-flex items-center gap-2 rounded bg-brandyellow px-6 py-3 text-sm font-semibold text-heading transition-transform ">
          Book another time
        </button>
      </div>
    </motion.div>
  );
}
